import { computed, observable } from 'mobx'
import { makeObservableRowBase, RowBase } from 'shared/ui/TableNew/model/RowBase'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { type IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'
import { EnumUserRoles } from 'entities/Users/model/types'

export interface IRowInviteCandidateParams {
  id: string
  email: string
  crmUser?: IntegrationCrmUser
  role?: EnumUserRoles
}

interface IRowInviteCandidateOrigin {
  id: string
}

export class RowInviteCandidate extends RowBase<IRowInviteCandidateOrigin> {
  readonly email: string
  readonly crmUser?: IntegrationCrmUser

  private _role: EnumUserRoles = EnumUserRoles.MEMBER
  private _selectedInboxIds: Set<number> | null | undefined = undefined
  private _isAssignLater = false
  private _hasInboxError = false

  constructor(params: IRowInviteCandidateParams) {
    super(params)

    this.email = params.email
    this.crmUser = params.crmUser

    if (params.role) {
      this._role = params.role
    }

    makeObservableRowBase(this, {
      _role: observable,
      _selectedInboxIds: observable,
      _isAssignLater: observable,
      _hasInboxError: observable,

      role: computed,
      selectedInboxIds: computed,
      hasInboxError: computed,
      isNewInbox: computed,
      isAssignLater: computed,
      hasEmptyInboxChoice: computed,
      avatarInfo: computed,
      displayName: computed,
    })
  }

  get role() {
    return this._role
  }

  get selectedInboxIds() {
    return this._selectedInboxIds
  }

  get hasInboxError() {
    return this._hasInboxError
  }

  get isNewInbox() {
    return this._selectedInboxIds === null
  }

  get isAssignLater() {
    return this._isAssignLater
  }

  get hasEmptyInboxChoice() {
    if (this.isNewInbox) return false
    if (this.isAssignLater) return false
    return !this._selectedInboxIds || this._selectedInboxIds.size === 0
  }

  get avatarInfo() {
    if (this.crmUser) {
      return this.crmUser.avatarInfo
    }
    return {
      firstName: this.email.charAt(0),
      lastName: '',
      image: '',
      color: this._getColorFromEmail(),
    }
  }

  get displayName() {
    if (this.crmUser) {
      return this.crmUser.name
    }
    return this.email
  }

  getSelectedInboxes = (allInboxes: Inbox[]): Inbox[] => {
    const selectedIds = this._selectedInboxIds
    if (!selectedIds) {
      return []
    }
    return allInboxes.filter((inbox) => selectedIds.has(inbox.id))
  }

  private _getColorFromEmail() {
    if (this.crmUser) {
      return this.crmUser.color
    }
    return 'var(--gray-a-15)'
  }

  setRole = (role: EnumUserRoles) => {
    this._role = role

    if (role === EnumUserRoles.VIEW_ONLY && this.isNewInbox) {
      this._selectedInboxIds = undefined
    }
  }

  setNewInbox = () => {
    this._isAssignLater = false
    this._selectedInboxIds = null
    this._hasInboxError = false
  }

  setAssignLater = () => {
    this._isAssignLater = true
    this._selectedInboxIds = undefined
    this._hasInboxError = false
  }

  setHasInboxError = (hasError: boolean) => {
    this._hasInboxError = hasError
  }

  private _addInbox = (inboxId: number) => {
    this._isAssignLater = false
    if (!this._selectedInboxIds) {
      this._selectedInboxIds = new Set([inboxId])
    } else {
      this._selectedInboxIds.add(inboxId)
    }
    this._hasInboxError = false
  }

  private _removeInbox = (inboxId: number) => {
    if (this._selectedInboxIds) {
      this._selectedInboxIds.delete(inboxId)
    }
  }

  toggleInbox = (inboxId: number) => {
    if (this._selectedInboxIds && this._selectedInboxIds.has(inboxId)) {
      this._removeInbox(inboxId)
    } else {
      this._addInbox(inboxId)
    }
  }
}
